import React, { useEffect, useState } from 'react'

import { addSeconds } from 'date-fns'
import { JwtPayload, jwtDecode } from 'jwt-decode'
import update from 'react-addons-update'
import OneSignalReact from 'react-onesignal'
import styled from 'styled-components'

import { APP_DEFAULT_STATE, AppPlugin } from '@api/local'
import { Button, Link, Paragraph, Spacer } from '@client/components/atoms'
import { TitleSeperator } from '@client/components/atoms/layout/TitleSeperator'
import { ResponsivePXValue, ResponsiveProperty, ZeroSpace, theme } from '@client/components/Theme'
import { DeviceContainer } from '@client/components/utility'
import { useConfig } from '@client/contexts/ConfigProvider'
import { SiteHelper } from '@client/lib/SiteHelper'
import { useEvents } from '@contexts/GTMProvider'
import { RegisteredUserDetailsFragment, UserDetailsDocument, UserDetailsFragment, useGetAppQuery, useRegisterCommunicationPlatformMutation, useRequestPhoneVerificationMutation, useUpdateUserMutation, useUserDetailsQuery, useUserSignUpMutation, useUserSocialSignUpMutation } from '@hooks/api'
import { AppleSocialButton, CheckBox, FacebookSocialButton, FieldData, Form, GoogleIdentitySocialButton, GoogleSocialButton, ModalMessageContainer, TextInput, useForm } from '@molecules/index'
import type { AppleProfile } from '@molecules/user/AppleSocialButton'
import { SocialLoginInput, SocialPlatformEnum, UserCommunicationPlatformEnum, UserSignUpInput } from '@uctypes/api/globalTypes'

const Container = styled.div<{$isNativeApplication: boolean}>`

  ${ResponsivePXValue('padding', '0 12px')}

  .input {
    ${ZeroSpace}
  }

  .check-input {
    div {
      font-family: gordita;
      font-weight: 400;
      ${ResponsivePXValue('font-size', { mobile: '8px', desktop: '16px' })}
      ${ResponsivePXValue('line-height', '16px')}
    }
  }
`

const NameContainer = styled.div`
  width: 100%;
  display: flex;
  ${ResponsiveProperty('flex-direction', { mobile: 'column', tablet: 'row', desktop: 'row' })}

  .name-input {
    ${ZeroSpace}
    flex-grow: 1;
  }
`

const NameSpacer = styled.div`
  ${ResponsivePXValue('width', { mobile: '0', tablet: '16px', desktop: '16px' })}
  ${ResponsivePXValue('height', { mobile: '16px', tablet: '0', desktop: '0' })}
`
const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
`
const TextLink = styled.a`
  color: ${theme.colors.slates.bitter};

  ${ResponsivePXValue('padding', '0 5px')}
`

const SocialButtonContainer = styled.div`
  display: flex;
  ${ResponsiveProperty('flex-direction', { mobile: 'column', desktop: 'row' })}
`
interface SignupState {
  communicationConsent: boolean
  phone: string
  email: string
  error: string
  hasGeneratedLead: boolean
  loggedSu: boolean
  platform: string
  firstName: string
  lastName: string
  appleFirstName: string
  appleLastName: string
}

const DEFAULT_STATE: SignupState = {
  communicationConsent: true,
  phone: '',
  email: '',
  error: '',
  firstName: '',
  lastName: '',
  appleFirstName: '',
  appleLastName: '',
  platform: 'UCOOK',
  loggedSu: true,
  hasGeneratedLead: false,
}
export interface SignupFormProps {
  onSignedUp: (hasCellphoneNumber: boolean, cellphoneIsVerified: boolean) => void
  onLogIn: () => void
}

export function SignupForm({ onSignedUp, onLogIn }: SignupFormProps): JSX.Element {

  const config = useConfig()
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const [signup, { loading: signupLoading }] = useUserSignUpMutation()
  const [addOneSignalData] = useRegisterCommunicationPlatformMutation()
  const [socialSignup, { loading: socialSignupLoading }] = useUserSocialSignUpMutation()
  const [requestPhoneVerification, { loading: requestPhoneVerificationLoading }] = useRequestPhoneVerificationMutation()
  const [updateUser] = useUpdateUserMutation()
  const { data: userDetailsData, loading: userDetailsLoading } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const [form] = useForm()
  const events = useEvents()

  const [state, setState] = useState<SignupState>({ ...DEFAULT_STATE })

  const isNativeApplication = appData?.app?.isNativeApplication ?? false
  const registeredUser = userDetailsData?.currentUser as UserDetailsFragment & RegisteredUserDetailsFragment
  const source = isNativeApplication ? 'mobile': 'web'

  const communicationText = 'I agree to receive information and offers from UCOOK'
  let newsletter = 'no'

  if (state.communicationConsent) {
    newsletter = 'yes'
  }

  useEffect(() => {
    form.setFieldsValue({
      communicationConsent: ['consent'],
    })
  }, [userDetailsLoading])

  useEffect(() => {
    if (!registeredUser) {
      return
    }

    if (state.platform !== SocialPlatformEnum.APPLE) {
      return
    }

    const missingName = !registeredUser.firstName && !registeredUser.lastName
    const hasAppleName = !!state.appleFirstName || !!state.appleLastName

    if (!missingName || !hasAppleName) {
      return
    }

    updateUser({
      variables: {
        id: registeredUser.id,
        input: {
          firstName: state.appleFirstName || registeredUser.firstName,
          lastName: state.appleLastName || registeredUser.lastName,
        },
      },
    }).finally(() => {
      setState((prevState) => ({
        ...prevState,
        appleFirstName: '',
        appleLastName: '',
      }))
    })
  }, [registeredUser, state.appleFirstName, state.appleLastName, state.platform, updateUser])

  const _handleSendPhoneVerification = async (): Promise<void> => {
    try {
      const response = await requestPhoneVerification({
        variables: {
          number: userDetailsData?.currentUser?.phone,
        },
        refetchQueries: [{ query: UserDetailsDocument }],
        awaitRefetchQueries: true,
      })

      if (response?.data?.success) {
        onSignedUp?.(true, userDetailsData?.currentUser?.phoneIsVerified)
      }

    } catch (e) {
      setState((prevState) => update(prevState, {
        error: { $set: e.message },
      }))

    }
  }

  const _handleSignupWithGoogle = async (token: string): Promise<void> => {
    const userDataFromGoogle: JwtPayload = jwtDecode(token)

    setState((prevState) => update(prevState, {
      platform: { $set: SocialPlatformEnum.GOOGLE },
      loggedSu: { $set: false },
    }))

    _handleSocialSignUp({
      platform: SocialPlatformEnum.GOOGLE,
      userId: userDataFromGoogle.sub,
      accessToken: token,
      expiry: addSeconds(new Date(), userDataFromGoogle.exp/ 1000),
    })

  }

  const _handleSignupWithFacebook = async (authResponse: fb.AuthResponse): Promise<void> => {
    setState((prevState) => update(prevState, {
      platform: { $set: SocialPlatformEnum.FACEBOOK },
      loggedSu: { $set: false },
    }))

    _handleSocialSignUp({
      platform: SocialPlatformEnum.FACEBOOK,
      userId: authResponse.userID,
      accessToken: authResponse.accessToken,
      expiry: addSeconds(new Date(), authResponse.expiresIn / 1000),
    })

  }

  const _handleSignupWithApple = async (token: string, profile?: AppleProfile): Promise<void> => {
    const userDataFromApple: JwtPayload = jwtDecode(token)

    setState((prevState) => update(prevState, {
      platform: { $set: SocialPlatformEnum.APPLE },
      loggedSu: { $set: false },
      appleFirstName: { $set: profile?.givenName || profile?.name?.split(' ')?.[0] || '' },
      appleLastName: { $set: profile?.familyName || profile?.name?.split(' ')?.slice(1).join(' ') || '' },
    }))

    _handleSocialSignUp({
      platform: SocialPlatformEnum.APPLE,
      userId: userDataFromApple.sub,
      accessToken: token,
      expiry: addSeconds(new Date(), userDataFromApple.exp / 1000),
    })
  }

  const _handleSocialSignUp = async (input: SocialLoginInput): Promise<void> => {
    try {
      const signupInput = {
        ...input,
        communicationConsent: state.communicationConsent,
      }
      await socialSignup({
        variables: {
          input: signupInput,
        },
        refetchQueries: SiteHelper.getUserRefetchQueries(),
        awaitRefetchQueries: true,
      })
    } catch (e) {
      setState((prevState) => update(prevState, {
        error: { $set: e.message },
      }))
    }
  }

  const _handleSignUp = async (input: UserSignUpInput): Promise<void> => {
    try {
      const signupInput = {
        ...input,
        communicationConsent: state.communicationConsent,
      }
      setState((prevState) => update(prevState, {
        loggedSu: { $set: false },
      }))
      await signup({
        variables: {
          input: signupInput,
        },
        refetchQueries: SiteHelper.getUserRefetchQueries(),
        awaitRefetchQueries: true,
      })

    } catch (e) {
      setState((prevState) => update(prevState, {
        error: { $set: e.message },
      }))
    }
  }

  const _handleChange = (changedFields: FieldData[]) => {
    changedFields.forEach((field) => {
      (field.name as string[]).forEach((name) => {
        if (name === 'communicationConsent') {
          setState((prevState) => ({ ...prevState, communicationConsent: field.value.length > 0 }))
        }
        if (name === 'phone') {
          setState((prevState) => ({ ...prevState, phone: field.value }))
        }
        if (name === 'email') {
          setState((prevState) => ({ ...prevState, email: field.value }))
        }
        if (name === 'firstName') {
          setState((prevState) => ({ ...prevState, firstName: field.value }))
        }
        if (name === 'lastName') {
          setState((prevState) => ({ ...prevState, lastName: field.value }))
        }
      })
    })
  }

  const _handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => { SiteHelper.validateAndSubmitFormOnEnter(event, form) }

  const _handleOneSignalInsert = async (input: {deviceId: string, platformId: string, platformType: UserCommunicationPlatformEnum}): Promise<void> => {
    try {
      await addOneSignalData({
        variables: input,
      })

      setState((prevState) => update(prevState, {
        hasBeenOneSignalInserted: { $set: true },
      }))

      OneSignalReact.login(userDetailsData?.currentUser?.id)

      // add to localVaraslogged in so we dont keep doing it
      AppPlugin.shared().setIsOneSignalLoggedIn(true)
    } catch (e) {
      setState((prevState) => update(prevState, {
        error: { $set: e.message },
      }))
    }
  }

  const _handleNativeOneSignal = async () => {
    const OneSignalNative = (await import('onesignal-cordova-plugin')).default

    const oneSignalPushId = await OneSignalNative.User.pushSubscription.getIdAsync()

    const userCommunicationData = registeredUser?.currentDevice?.userCommunicationPlatformData
    const communicationData = Array(userCommunicationData)[0]
    let isUserCommunicationPlatformRegistered = false

    for (let i = 0; i < Object.keys(communicationData)?.length; i++) {
      if (Object.values(communicationData)[i].platformType === UserCommunicationPlatformEnum.ONE_SIGNAL) {
        isUserCommunicationPlatformRegistered = true
        break
      }
    }

    if (!isUserCommunicationPlatformRegistered) {
      try {
        await addOneSignalData({
          variables: {
            deviceId: registeredUser.currentDevice.id,
            platformId: oneSignalPushId,
            platformType: UserCommunicationPlatformEnum.ONE_SIGNAL,
          },
        })

        setState((prevState) => update(prevState, {
          hasBeenOneSignalInserted: { $set: true },
        }))

        await OneSignalNative.login(registeredUser.id)
        AppPlugin.shared().setIsOneSignalLoggedIn(true)
      } catch (e) {
        setState((prevState) => update(prevState, {
          error: { $set: e.message },
        }))
      }
    }
  }

  useEffect(() => {
    const currentUser = userDetailsData?.currentUser

    if (currentUser?.__typename === 'RegisteredUser') {
      if (currentUser?.phone) {
        _handleSendPhoneVerification()
        const email = currentUser.email
        const mobile = currentUser.phone
        if (state.loggedSu === false) {
          events.hasSignedUp(state.email, state.phone, state.platform, state.firstName, state.lastName, newsletter, source)
          setState((prevState) => update(prevState, {
            loggedSu: { $set: true },
          }))
        }

        if (newsletter && state.hasGeneratedLead === false) {
          setTimeout(() => {
            events.hasGeneratedLead(email, mobile, 'Signup')
            setState((prevState) => update(prevState, {
              hasGeneratedLead: { $set: true },
            }))
          }, 2000)
        }
      } else {
        onSignedUp?.(false, false)
      }

      if (isNativeApplication) {
        _handleNativeOneSignal()
      } else {
        const isSubscribed = OneSignalReact.User.PushSubscription.optedIn
        const userCommunicationData = registeredUser?.currentDevice?.userCommunicationPlatformData
        const communicationData = Array(userCommunicationData)[0]
        let isUserCommunicationPlatformRegistered = false

        for (let i = 0; i < Object.keys(communicationData)?.length; i++) {
          if (Object.values(communicationData)[i].platformType === UserCommunicationPlatformEnum.ONE_SIGNAL) {
            isUserCommunicationPlatformRegistered = true
            break
          }
        }

        if (!isUserCommunicationPlatformRegistered && isSubscribed) {
          _handleOneSignalInsert({
            deviceId: registeredUser.currentDevice.id,
            platformId: registeredUser.id,
            platformType: UserCommunicationPlatformEnum.ONE_SIGNAL,
          })
        } else {
          OneSignalReact.login(userDetailsData?.currentUser?.id)
          AppPlugin.shared().setIsOneSignalLoggedIn(true)
        }
      }
    }

  }, [userDetailsData?.currentUser])

  const loading = signupLoading || socialSignupLoading || userDetailsLoading || requestPhoneVerificationLoading

  return (
    <Container $isNativeApplication={isNativeApplication}>
      <Spacer universal='8px' />

      <Form form={form} onFieldsChange={_handleChange} onFinish={_handleSignUp} disabled={loading}>
        <SocialButtonContainer>

          <If condition={isNativeApplication}>
            <AppleSocialButton
              disabled={loading}
              action='SIGN UP'
              didAuthenticateWithApple={_handleSignupWithApple} />
            <DeviceContainer $mobile $tablet>
              <Spacer universal='8px' />
            </DeviceContainer>
            <DeviceContainer $desktop>
              <Spacer universal='16px' variant='horizontal' />
            </DeviceContainer>
            <GoogleSocialButton
              disabled={loading}
              action='SIGN UP'
              didAuthenticateWithGoogle={_handleSignupWithGoogle} />
          </If>
          <If condition={!isNativeApplication}>
            <GoogleIdentitySocialButton
            message='signup'
            disabled={loading}
            didAuthenticateWithGoogle={_handleSignupWithGoogle} />
          </If>

          <DeviceContainer $mobile $tablet>
            <Spacer universal='8px' />
          </DeviceContainer>
          <DeviceContainer $desktop>
            <Spacer universal='16px' variant='horizontal' />
          </DeviceContainer>
          <FacebookSocialButton
            disabled={loading}
            didAuthenticateWithFacebook={_handleSignupWithFacebook} />
        </SocialButtonContainer>

        <Spacer universal='16px' />
        <TitleSeperator title='OR' />
        <Spacer universal='8px' />

        <NameContainer>
          <TextInput
            onKeyDown={_handleKeyDown}
            name='firstName'
            placeholder='Enter your name'
            rules={[{ required: true, message: 'Please input a first name' }]}
            variant='text'
            className='name-input' />
          <NameSpacer />
          <TextInput
            onKeyDown={_handleKeyDown}
            name='lastName'
            placeholder='Enter your surname'
            rules={[{ required: true, message: 'Please input a last name' }]}
            variant='text'
            className='name-input' />
        </NameContainer>
        <Spacer universal='16px' />
        <TextInput
          onKeyDown={_handleKeyDown}
          name='email'
          placeholder='Enter your email'
          rules={[{ required: true, message: 'Please input an email' }]}
          variant='email'
          className='input' />
        <Spacer universal='16px' />
        <TextInput
          onKeyDown={_handleKeyDown}
          name='phone'
          placeholder='Enter your cell number'
          rules={[{ required: true, message: 'Please input a cell number' }]}
          variant='phone'
          className='input' />
        <Spacer universal='16px' />
        <TextInput
          onKeyDown={_handleKeyDown}
          name='password'
          placeholder='Enter your password'
          rules={[{ required: true, message: 'Please input a password' }]}
          variant='password'
          className='input' />
        <CheckBox
          className='check-input'
          name='communicationConsent'
          label={communicationText}
          showLabel={false}
          showOptional={false}
          options={[{ title: communicationText, value: 'consent' }]} />
        <Spacer universal='16px' />
        <If condition={state.error !== ''}>
          <ModalMessageContainer>
            <Paragraph variant='p1' align='center' className='error'>{state.error}</Paragraph>
          </ModalMessageContainer>
          <Spacer universal='24px' />
        </If>
        <Button
          title='SIGN UP'
          disabled={loading}
          color='black'
          fullWidth
          loading={loading}
          onClick={() => form.submit()}/>
        <Spacer mobile='8px' desktop='16px' />
        <Paragraph
          variant='p2'
          color={theme.colors.slates.bitter}
          align='center'>
          By continuing, you acknowledge that you have read and agreed to the
          <TextLink href='https://ucook.co.za/terms' target='_blank'>terms and conditions</TextLink>
          and
          <TextLink href='https://ucook.co.za/privacy-policy' target='_blank'>privacy policy</TextLink>
          of UCOOK.
        </Paragraph>
        <Spacer mobile='8px' desktop='16px' />
        <FooterContainer>
          <Paragraph variant='p2'>Already have an account?</Paragraph>
          <Spacer universal='8px' variant='horizontal' />
          <Link variant='l2' decoration='underline' onClick={onLogIn}> Login </Link>
        </FooterContainer>
      </Form>
    </Container>
  )
}
